import { useTranslation } from 'react-i18next';
import { useRef, useState, useEffect, useContext } from 'react';
import DetailsSidebar from '../../component/detailssidebar/DetailsSidebar';
import Dropdown from '../../component/input/Dropdown';
import InputText from '../../component/input/InputText';
import InputAmount from '../../component/input/InputAmount';
import InputSwitch from '../../component/input/InputSwitch';
import { DeviceService } from '../../service/DeviceService';
import { RouteService } from '../../service/RouteService';
import { CashLevelService } from '../../service/CashLevelService';
import { MessageContext } from '../../context/MessageContext';
import './CashOperationSidebar.scss';
import useValidator from '../../utils/validator/useValidator';
const CashOperationSidebar = ({ visible, onHide, onSuccess, viewMode, cashOperationId, operationType }) =>{
    const { t } = useTranslation();
    const isMounted = useRef(false);
    const prevSpecialistIdRef = useRef(null);
    const [deviceOptions, setDeviceOptions] = useState([]);
    const [specialistOptions, setSpecialistOptions] = useState([]);
    const [routeOptions, setRouteOptions] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const validator = useValidator();
    const [mainKey, setMainKey] = useState(0);
    const [form, setForm] = useState({
        deviceId: null,
        specialistId: null,
        routeId: null,
        barcode: '',
        cashAmount: null,
        leaveChangeInDevice: false,
        amountLeftInDevice: null
    });
    const { showErrorMessage, showSuccessMessage } = useContext(MessageContext);

    const fetchDeviceOptions = async () => {
        try {
            const devices = await DeviceService.getDevicesForCashOperations();
            if (isMounted.current) {
                setDeviceOptions(devices.map(device => ({ id: device.id, label: device.name })));
            }
        } catch (err) {
            showErrorMessage(t('cashoperations.cashOperationSidebar.error.fetchDeviceOptions'));
        }
    };

    const fetchSpecialistOptions = async () => {
        try {
            const response = await RouteService.getTechniciansList();
            const techniciansData = Array.isArray(response) ? response : (response?.data || []);
            if (isMounted.current) {
                setSpecialistOptions(techniciansData.map(t => ({ id: t.id, label: t.name })));
            }
        } catch (err) {
            showErrorMessage(t('cashoperations.cashOperationSidebar.error.fetchSpecialistOptions'));
        }
    };

    const fetchRouteOptions = async (technicianId = null) => {
        try {
            const response = await RouteService.getPlannedRoutesList(technicianId);
            const routesData = Array.isArray(response) ? response : (response?.data || []);
            if (isMounted.current) {
                setRouteOptions(routesData.map(item => ({
                    id: item.route?.id,
                    label: item.route?.name,
                    technicianId: item.technician?.id,
                    technicianName: item.technician?.name
                })));
            }
        } catch (err) {
            showErrorMessage(t('cashoperations.cashOperationSidebar.error.fetchRouteOptions'));
        }
    };

    useEffect(() => {
        isMounted.current = true;
        if (visible) {
            fetchDeviceOptions();
            fetchSpecialistOptions();
            fetchRouteOptions();
            // Reset form when sidebar opens
            setForm({
                deviceId: null,
                specialistId: null,
                routeId: null,
                barcode: '',
                cashAmount: null,
                leaveChangeInDevice: false,
                amountLeftInDevice: null
            });
            prevSpecialistIdRef.current = null;
            validator.hideMessages();
        }
        return () => {
            isMounted.current = false;
        };
    }, [visible]);

    // Re-fetch route options when specialist changes (routeId is cleared only in specialist dropdown onChange)
    useEffect(() => {
        if (visible && isMounted.current && prevSpecialistIdRef.current !== form.specialistId) {
            prevSpecialistIdRef.current = form.specialistId;
            fetchRouteOptions(form.specialistId);
        }
    }, [form.specialistId, visible]);
    
    const getTitle = () => {
        if (operationType === 'cashDeposit') {
            return t('cashoperations.cashOperationSidebar.titleDeposit');
        } else if (operationType === 'cashWithdrawal') {
            return t('cashoperations.cashOperationSidebar.titleWithdrawal');
        } else if (operationType === 'cashCount') {
            return t('cashoperations.cashOperationSidebar.titleCashCount');
        }
        return t('cashoperations.cashOperationSidebar.title');
    };

    const getDescription = () => {
        if (operationType === 'cashDeposit') {
            return t('cashoperations.cashOperationSidebar.descriptionDeposit');
        } else if (operationType === 'cashWithdrawal') {
            return t('cashoperations.cashOperationSidebar.descriptionWithdrawal');
        } else if (operationType === 'cashCount') {
            return t('cashoperations.cashOperationSidebar.descriptionCashCount');
        }
        return null;
    };

    const getEventType = () => {
        if (operationType === 'cashDeposit') {
            return 'CASH_DEPOSIT';
        } else if (operationType === 'cashWithdrawal') {
            return 'CASH_WITHDRAWAL';
        } else if (operationType === 'cashCount') {
            return 'CASH_COUNT';
        }
        return null;
    };

    const handleSave = () => {
        const eventType = getEventType();
        if (!eventType) {
            showErrorMessage(t('cashoperations.cashOperationSidebar.error.invalidOperationType'));
            return;
        }

        if (!validator.allValid()) {
            validator.showMessages();
            setMainKey(prev => prev + 1);
            return;
        }

        if (!form.deviceId) {
            showErrorMessage(t('cashoperations.cashOperationSidebar.error.deviceIdRequired'));
            return;
        }

        const isWithdrawalWithLeaveChange = operationType === 'cashWithdrawal' && form.leaveChangeInDevice;
        if (isWithdrawalWithLeaveChange) {
            if (!form.cashAmount || form.cashAmount <= 0) {
                showErrorMessage(t('cashoperations.cashOperationSidebar.error.amountRequired'));
                return;
            }
            if (!form.amountLeftInDevice || form.amountLeftInDevice < 0) {
                showErrorMessage(t('cashoperations.cashOperationSidebar.error.amountLeftInDeviceRequired'));
                return;
            }
        } else if (operationType === 'cashCount') {
            if (form.cashAmount == null || form.cashAmount === '' || Number(form.cashAmount) < 0) {
                showErrorMessage(t('cashoperations.cashOperationSidebar.error.amountRequired'));
                return;
            }
        } else if (operationType === 'cashWithdrawal') {
            // For cash withdrawal without "leave change in device" we don't require cashAmount
        } else {
            if (!form.cashAmount || form.cashAmount <= 0) {
                showErrorMessage(t('cashoperations.cashOperationSidebar.error.amountRequired'));
                return;
            }
        }

        setIsSaving(true);

        if (operationType === 'cashCount') {
            const setBalanceRequest = {
                deviceId: form.deviceId,
                cashAmount: form.cashAmount,
                ...(form.specialistId && { specialistId: form.specialistId })
            };
            CashLevelService.setBalance(setBalanceRequest)
                .then(() => {
                    if (isMounted.current) {
                        showSuccessMessage(t('cashoperations.cashOperationSidebar.success.operation'));
                        if (onSuccess) onSuccess();
                        if (onHide) onHide();
                    }
                })
                .catch((err) => {
                    console.error('Set balance error:', err);
                    if (isMounted.current) {
                        showErrorMessage(t('cashoperations.cashOperationSidebar.error.operation'));
                    }
                })
                .finally(() => {
                    if (isMounted.current) setIsSaving(false);
                });
            return;
        }

        const baseRequest = {
            deviceId: form.deviceId,
            bagId: form.barcode || null,
            specialistId: form.specialistId || null,
            routeId: form.routeId || null
        };

        if (isWithdrawalWithLeaveChange) {
            const withdrawalRequest = { ...baseRequest, cashAmount: form.cashAmount, eventType: 'CASH_WITHDRAWAL' };
            const depositRequest = { ...baseRequest, cashAmount: form.amountLeftInDevice, eventType: 'CASH_DEPOSIT' };
            CashLevelService.registerCashOperation(withdrawalRequest)
                .then(() => CashLevelService.registerCashOperation(depositRequest))
                .then(() => {
                    if (isMounted.current) {
                        showSuccessMessage(t('cashoperations.cashOperationSidebar.success.withdrawalAndDeposit'));
                        if (onSuccess) onSuccess();
                        if (onHide) onHide();
                    }
                })
                .catch((err) => {
                    console.error('Cash operation error:', err);
                    if (isMounted.current) {
                        showErrorMessage(t('cashoperations.cashOperationSidebar.error.operation'));
                    }
                })
                .finally(() => {
                    if (isMounted.current) setIsSaving(false);
                });
            return;
        }

        const request = {
            ...baseRequest,
            ...(operationType !== 'cashWithdrawal' ? { cashAmount: form.cashAmount } : {}),
            eventType: eventType
        };

        CashLevelService.registerCashOperation(request)
            .then(() => {
                const successKey = operationType === 'cashDeposit'
                    ? 'cashoperations.cashOperationSidebar.success.deposit'
                    : operationType === 'cashWithdrawal'
                    ? 'cashoperations.cashOperationSidebar.success.withdrawal'
                    : 'cashoperations.cashOperationSidebar.success.cashCount';
                showSuccessMessage(t(successKey));
                if (onSuccess) onSuccess();
                if (onHide) onHide();
                setIsSaving(false);
            })
            .catch((err) => {
                console.error('Cash operation error:', err);
                const errorKey = operationType === 'cashDeposit'
                    ? 'cashoperations.cashOperationSidebar.error.deposit'
                    : operationType === 'cashWithdrawal'
                    ? 'cashoperations.cashOperationSidebar.error.withdrawal'
                    : 'cashoperations.cashOperationSidebar.error.cashCount';
                showErrorMessage(t(errorKey));
                setIsSaving(false);
            });
    };

    return (
        <DetailsSidebar
            className="cash-operation-sidebar"
            visible={visible}
            onHide={onHide}
            onSave={handleSave}
            contentKey={mainKey}
            isSaving={isSaving}
            viewMode={viewMode}
            cashOperationId={cashOperationId}
            title={getTitle()}
            >
            {getDescription() && (
                <p className="description">
                    {getDescription()}
                </p>
            )}
            <Dropdown 
                id="deviceId" 
                label={t('cashoperations.cashOperationSidebar.deviceId')} 
                options={deviceOptions || []} 
                optionLabel="label"
                optionValue="id"
                value={form.deviceId}
                onChange={(e) => setForm(prev => ({ ...prev, deviceId: e.value }))}
                validator={validator}
                validations="required"
            />
        {operationType !== 'cashCount' && (
            <Dropdown 
                id="specialistId" 
                label={t('cashoperations.cashOperationSidebar.specialistId')} 
                options={specialistOptions || []} 
                optionLabel="label"
                optionValue="id"
                value={form.specialistId}
                onChange={(e) => {
                    setForm(prev => ({ ...prev, specialistId: e.value, routeId: null }));
                }}
                showClear

            />
            )}
            {operationType !== 'cashCount' && (
                <Dropdown 
                    id="routeId" 
                    label={t('cashoperations.cashOperationSidebar.routeId')} 
                    options={routeOptions || []} 
                    optionLabel="label"
                    optionValue="id"
                    value={form.routeId}
                    onChange={(e) => {
                        const selectedOption = (routeOptions || []).find(o => o.id === e.value);
                        setForm(prev => ({
                            ...prev,
                            routeId: e.value,
                            specialistId: selectedOption?.technicianId ?? prev.specialistId
                        }));
                    }}
                    showClear
                />
            )}
            {operationType !== 'cashCount' && (
                <InputText 
                    id='barcode' 
                    label={t('cashoperations.cashOperationSidebar.barcode')} 
                    value={form.barcode}
                    onChange={(e) => setForm(prev => ({ ...prev, barcode: e.target.value }))}
                    // validator={validator}
                    // validations="required"
                />
            )}
{operationType !== 'cashWithdrawal' && (
            <InputAmount 
                id='cashAmount' 
                label={t('cashoperations.cashOperationSidebar.cashAmount')} 
                value={form.cashAmount}
                onChange={(e) => setForm(prev => ({ ...prev, cashAmount: e.target.value }))}
                validator={validator}
                validations="required"
            />
            )}
            {operationType === 'cashWithdrawal' && (
                <>
                    <InputSwitch
                        label={t('cashoperations.cashOperationSidebar.leaveChangeInDevice')}
                        checked={form.leaveChangeInDevice}
                        onChange={(e) => setForm(prev => ({ ...prev, leaveChangeInDevice: !!e.value }))}
                    />
                    {form.leaveChangeInDevice && (
                        <InputAmount
                            id="amountLeftInDevice"
                            label={t('cashoperations.cashOperationSidebar.amountLeftInDevice')}
                            value={form.amountLeftInDevice}
                            onChange={(e) => setForm(prev => ({ ...prev, amountLeftInDevice: e.target?.value ?? e.value }))}
                            validator={validator}
                            validations="required"
                        />
                    )}
                </>
            )}
         
        </DetailsSidebar>
    );
};
export default CashOperationSidebar;